import { useFeature } from '@growthbook/growthbook-react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { ENTITIES_SORTING_CONFIG_URL } from '../../constants/api';
import FEATURES from '../../constants/featureToggling';
import { isDebugModeSelector } from '../../redux/reducers/app/initialization';
import { userCurrencyNameSelector } from '../../redux/reducers/app/params';
import { getAPI } from '../../services/API';
import { sdkErrorHandler } from '../../utils';
import { getVariation } from '../../utils/configVariation';
import { sortProvidersByReference } from '../../utils/sorting';
import { DEFAULT_QUERY_LIMIT, DEFAULT_QUERY_PARAMS } from './constants';
import { useCommonHeaders } from './useCommonHeaders';
import { useQueryGeoLocation } from './useQueryGeoLocation';
import useRequestConfig from './useRequestConfig';

export const QUERY_KEY_PROVIDER = 'provider';
export const QUERY_KEY_PROVIDERS = 'providers';
export const QUERY_KEY_PROVIDERS_SORTING = 'providers-sorting';
export const QUERY_KEY_INFINITY_PROVIDERS = 'infinity-providers';

const useProvidersSortingPredicates = () => {
  const currency = useSelector(userCurrencyNameSelector);
  const { data: geo, isLoading } = useQueryGeoLocation();

  return { data: { currency, geo: geo?.toUpperCase() }, isLoading };
};

export const useQueryProvidersSorting = ({ enabled }: { enabled: boolean }) => {
  const { data: predicates, isLoading: isLoadingPredicates } =
    useProvidersSortingPredicates();

  const fetch = ({ signal }: { signal?: AbortSignal }) =>
    getAPI()
      .fetch(ENTITIES_SORTING_CONFIG_URL, { signal })
      .catch(
        sdkErrorHandler({
          source: `Error fetch to ${ENTITIES_SORTING_CONFIG_URL}`,
        })
      );

  const query = useQuery([QUERY_KEY_PROVIDERS_SORTING, predicates], fetch, {
    enabled: enabled && !isLoadingPredicates,
    select(data: any) {
      if (!data) {
        return data;
      }

      return getVariation(
        {
          rules: data?.providers,
          defaultVariation: null,
        },
        predicates
      );
    },
  });

  return { ...query, isLoading: isLoadingPredicates || query.isLoading };
};

export const useQuerySortedProviders = (
  query = {},
  queryOptions: any = { enabled: true }
) => {
  const isSortingEnabled = useFeature(FEATURES.PROVIDERS_SORTING).on;

  const { data: sortingConfig, isSuccess } = useQueryProvidersSorting({
    enabled: isSortingEnabled,
  });

  const isDisabled = !isSortingEnabled || !sortingConfig;

  return useQueryProviders(isDisabled ? query : { ...query, _limit: -1 }, {
    ...(!isDisabled && {
      select(data) {
        const items = sortProvidersByReference(sortingConfig, data.items);

        return {
          ...data,
          items: query['_limit'] > 0 ? items.slice(0, query['_limit']) : items,
        };
      },
    }),
    enabled:
      queryOptions?.enabled && isSortingEnabled
        ? isSuccess
        : queryOptions?.enabled,
  });
};

export const useQueryProviders = (query = {}, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...query,
  };

  const fetch = ({ signal }: { signal?: AbortSignal }) =>
    getAPI()
      // @ts-ignore
      .providers.getAll({ query: queryParams, signal, ...requestConfig })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          query: queryParams,
          source: `SDK API providers.getAll`,
        })
      );

  return useQuery(
    [QUERY_KEY_PROVIDERS, queryParams, requestConfig.headers],
    fetch,
    queryOptions
  );
};

export const useQueryProvider = (providerId: string, queryOptions = {}) => {
  const requestConfig = useRequestConfig();

  const fetch = ({ signal }: { signal?: AbortSignal }) =>
    getAPI()
      // @ts-ignore
      .providers.getOne(providerId, { signal, ...requestConfig })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          providerId,
          source: `SDK API providers.getOne`,
        })
      );

  return useQuery(
    [QUERY_KEY_PROVIDER, providerId, requestConfig.headers],
    fetch,
    queryOptions
  );
};

export const useInfinityQueryProviders = (query = {}, queryOptions = {}) => {
  const headers = useCommonHeaders();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    _start: 0,
    _limit: DEFAULT_QUERY_LIMIT,
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...query,
  };

  const fetch = ({
    pageParam = 0,
    signal,
  }: {
    pageParam?: number;
    signal?: AbortSignal;
  }) => {
    const fetchQueryParams = {
      ...queryParams,
      _start: pageParam * queryParams._limit,
    };
    return (
      getAPI()
        // @ts-ignore
        .providers.getAll({
          signal,
          headers,
          query: fetchQueryParams,
        })
        .catch(
          sdkErrorHandler({
            queryOptions,
            headers,
            query: fetchQueryParams,
            source: `SDK API providers.getAll`,
          })
        )
    );
  };

  const infiniteQuery = useInfiniteQuery(
    [QUERY_KEY_INFINITY_PROVIDERS, queryParams],
    fetch,
    {
      ...queryOptions,
      select: (data) => {
        return {
          pages: data.pages.map((response) => response.items),
          pageParams: data.pageParams,
          totalItems: data.pages?.[0]?.totalItems ?? 0,
        };
      },
      getNextPageParam: (pageData) => {
        return pageData.totalPages > pageData.currentPage
          ? pageData.currentPage
          : undefined;
      },
    }
  );

  const items = (infiniteQuery.data?.pages || []).flat();
  // @ts-ignore
  const totalItems = infiniteQuery.data?.totalItems;

  return { ...infiniteQuery, data: { items, totalItems } };
};
